<template>
  <section id="steps" class="steps container">
    <div
      v-for="step in steps"
      :key="`${stepsRef}-${step.year}`"
      class="step"
      :ref="stepsRef"
      :data-step="step.year"
      :data-members="step.countries ? true : false"
    >
      <DefaultCard v-bind:step="step" v-if="step.milestones" />
      <MemberCard
        v-if="step.countries"
        :year="step.year"
        :countries="step.countries"
        @globe-toggle="globeToggle"
        @member-toggle="memberToggle"
      />
    </div>
  </section>
</template>

<script>
import gsap from "gsap";
import DefaultCard from "./DefaultCard.vue";
import MemberCard from "./MemberCard.vue";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

export default {
  components: {
    DefaultCard,
    MemberCard,
  },
  props: {
    steps: {
      type: Array,
      required: false,
    },
    stepsRef: {
      type: String,
      required: true,
    },
    header: {
      type: Boolean,
      required: false,
    },
    footer: {
      type: Boolean,
      required: false,
    },
    selectedDecade: {
      required: false,
    },
  },
  mounted() {
    this.init();
  },
  watch: {
    selectedDecade(curr) {
      const steps = this.$refs[this.stepsRef];
      const el = steps.find((d) => curr <= Number(d.dataset.step));
      el.scrollIntoView();
    },
  },
  methods: {
    init() {
      const { stepsRef } = this;
      const steps = this.$refs[stepsRef];
      const duration = 0.5;
      steps.forEach((step, idx) => {
        ScrollTrigger.create({
          trigger: step,
          scroller: "#timeline",
          start: () => this.startPos(idx, step),
          end: () => this.endPos(step),
          onEnter: (e) => gsap.to(e.trigger, { opacity: 1, duration }),
          onEnterBack: (e) => gsap.to(e.trigger, { opacity: 1, duration }),
          onLeave: (e) => gsap.to(e.trigger, { opacity: 0.1, duration }),
          onLeaveBack: (e) => gsap.to(e.trigger, { opacity: 0.1, duration }),
          onToggle: (self) => this.scrollToggled(self),
          // markers: step.dataset.step === "2024" ? true : false,
          id: `steps-${idx}`,
        });
      });

      // Show decade tracker
      gsap.to("#year-nav-wrap", {
        scrollTrigger: {
          trigger: "#steps",
          scroller: "#timeline",
          toggleActions: "play none none reset",
          start: "top top",
          end: "top top",
        },
        ease: "power1.out",
        opacity: 1,
      });
    },
    scrollToggled(self) {
      if (self.isActive) {
        let step = self.trigger.dataset.step;
        this.$emit("step-scrolled", step);
        if (step === "2024") {
          gsap.to(".globe-wrapper", {
            opacity: 1,
            duration: 0.3,
            ease: "power1.out",
          });
          gsap.to(".member-label-container", {
            opacity: 0,
            duration: 0.3,
            ease: "power1.out",
          });
        }
      }
    },
    startPos(idx, step) {
      let margin = 80; //padding top
      let trigger = "80";
      let scroller;
      if (idx === 0) {
        scroller = "50%";
      } else {
        const prevElement = step.previousElementSibling;
        const firstchild = prevElement.firstChild;
        const lastSiblingHasMemberCard = prevElement.dataset.members;
        if (lastSiblingHasMemberCard) {
          // Previous step has member card
          if (prevElement.children.length === 1) {
            /**
             * Previous step only has member card.
             *
             * Prev card's end trigger is at 50% so it's starting scroller pos
             * needs to be at half of prev card's height
             */
            scroller = prevElement.offsetHeight * 0.75 + margin;
          } else {
            // Previous step has member & milestone cards
            if (firstchild.children.length > 1) {
              scroller = `25%+=${firstchild.lastChild.clientHeight + margin}px`;
            } else {
              scroller = `25%+=${
                firstchild.firstChild.clientHeight + margin
              }px`;
            }
          }
        } else {
          // Previous step does not have member card
          if (firstchild.children.length > 1) {
            // Previous step's milestone length is greater than 1
            scroller = firstchild.lastChild.clientHeight;
          } else {
            // Previous step's milestone length equals to 1
            scroller = prevElement.offsetHeight;
          }
        }
      }

      return `${trigger} ${scroller}`;
    },
    endPos(step) {
      let trigger = "80";
      let scroller = 0;
      const firstChild = step.firstChild;
      const hasMemberCard = step.dataset.members;

      if (hasMemberCard) {
        // Step has member card
        if (step.children.length === 1) {
          // Member card only
          trigger = "50%";
        } else {
          // Member & milestone card
          trigger = step.clientHeight - firstChild.lastChild.clientHeight;
          scroller = "25%";
        }
      } else {
        // Step does not have member card
        if (firstChild.children.length > 1) {
          trigger = step.clientHeight - firstChild.lastChild.clientHeight;
        }
      }
      return `${trigger} ${scroller}`;
    },
    globeToggle(value) {
      this.$emit("globe-toggle", value);
    },
    memberToggle(value) {
      this.$emit("member-toggle", value);
    },
  },
};
</script>

<style>
.steps {
  position: relative;
  top: 0;
  left: 0;
  width: 100%;
  margin-top: calc((-100vh + 60px));
}
.step {
  opacity: 0.1;
  display: flex;
  flex-flow: column;
}
</style>
